import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatDate } from '../../utils/api'
import { MetricsRow } from '../shared'
import '../../styles/pages.css'

function PIMBrands() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Stats
  const [stats, setStats] = useState({
    brand_count: 0,
    master_product_count: 0,
    variant_count: 0,
    needs_review_count: 0
  })

  // Brands data
  const [brands, setBrands] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  // Add brand modal
  const [showAddModal, setShowAddModal] = useState(false)
  const [newBrandName, setNewBrandName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [statsRes, brandsRes] = await Promise.all([
        api.get('/api/pim/stats'),
        api.get('/api/pim/brands')
      ])
      setStats(statsRes)
      setBrands(brandsRes.brands || [])
    } catch (err) {
      console.error('Failed to load data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Filter brands by search term
  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  function handleRowClick(brand) {
    navigate(`/pim/brands/${brand.brand_id}`)
  }

  async function handleAddBrand(e) {
    e.preventDefault()
    if (!newBrandName.trim()) {
      setError('Brand name is required')
      return
    }

    setCreating(true)
    setError(null)

    try {
      const response = await api.post('/api/pim/brands', { name: newBrandName.trim() })
      setSuccess(`Brand "${response.name}" created successfully`)
      setNewBrandName('')
      setShowAddModal(false)
      // Reload data
      await loadData()
    } catch (err) {
      console.error('Failed to create brand:', err)
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const metrics = [
    { label: 'Brands', value: stats.brand_count, color: 'blue' },
    { label: 'Master Products', value: stats.master_product_count, color: 'green' },
    { label: 'Variants', value: stats.variant_count, color: 'purple' },
    { label: 'Needs Review', value: stats.needs_review_count, color: stats.needs_review_count > 0 ? 'red' : '' }
  ]

  return (
    <>
      <MetricsRow metrics={metrics} />

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setError(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="card" style={{ background: 'var(--color-success-light)', padding: 'var(--space-4)', marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          {success}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search and Add */}
      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="filter-group" style={{ flex: 1, minWidth: 250 }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search brands..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            + Add Brand
          </button>
        </div>
        {filteredBrands.length !== brands.length && (
          <div className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
            Showing {filteredBrands.length} of {brands.length} brands
          </div>
        )}
      </div>

      {/* Brands Table */}
      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">
              {brands.length === 0 ? 'No Brands' : 'No Matching Brands'}
            </div>
            <div className="empty-state-description">
              {brands.length === 0
                ? 'Click "Add Brand" to create your first brand.'
                : 'Try adjusting your search term.'
              }
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Brand Name</th>
                <th style={{ textAlign: 'center' }}>Products</th>
              </tr>
            </thead>
            <tbody>
              {filteredBrands.map(brand => (
                <tr
                  key={brand.brand_id}
                  onClick={() => handleRowClick(brand)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span style={{ fontWeight: 500 }}>{brand.name}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${brand.master_product_count > 0 ? 'badge-info' : 'badge-neutral'}`}>
                      {brand.master_product_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Brand Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Brand</h3>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleAddBrand}>
              <div className="modal-body">
                <div className="filter-group">
                  <label className="filter-label">Brand Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newBrandName}
                    onChange={e => setNewBrandName(e.target.value)}
                    placeholder="Enter brand name..."
                    autoFocus
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false)
                    setNewBrandName('')
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating || !newBrandName.trim()}
                >
                  {creating ? 'Creating...' : 'Create Brand'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal styles (inline for simplicity) */}
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: var(--color-bg);
          border-radius: var(--radius-lg);
          width: 100%;
          max-width: 400px;
          box-shadow: var(--shadow-lg);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-4);
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h3 {
          margin: 0;
        }
        .modal-body {
          padding: var(--space-4);
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-2);
          padding: var(--space-4);
          border-top: 1px solid var(--color-border);
        }
      `}</style>
    </>
  )
}

export default PIMBrands
